<template>
  <div class="submissions-list">
    <div v-if="loading" class="loading">Loading submissions...</div>
    <div v-else-if="error" class="error-msg">{{ error }}</div>
    <div v-else-if="submissions.length === 0" class="empty">
      <div class="empty-icon">✓</div>
      <div>No pending submissions</div>
      <div class="empty-sub">All company resource submissions have been reviewed.</div>
    </div>

    <template v-else>
      <div class="list-meta">{{ pagination.total }} pending submission{{ pagination.total !== 1 ? 's' : '' }}</div>

      <table class="table">
        <thead>
          <tr>
            <th>Resource</th>
            <th>Company</th>
            <th>Type</th>
            <th>Audience</th>
            <th>Submitted</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="res in submissions" :key="res.id">
            <td class="col-resource">
              <div class="resource-title">{{ res.title }}</div>
              <div v-if="res.description" class="resource-desc">{{ truncate(res.description, 80) }}</div>
            </td>
            <td>{{ res.submitted_by_company?.name || '—' }}</td>
            <td>{{ res.type || '—' }}</td>
            <td>
              <span v-if="res.target_audience" :class="['badge-audience', `aud-${res.target_audience}`]">
                {{ res.target_audience }}
              </span>
              <span v-else>—</span>
            </td>
            <td>{{ formatDate(res.updated_at) }}</td>
            <td class="actions">
              <button @click="$emit('review', res.id)" class="btn-review">Review</button>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Pagination -->
      <div v-if="pagination.last_page > 1" class="pagination">
        <button
          :disabled="pagination.current_page === 1"
          @click="loadPage(pagination.current_page - 1)"
          class="page-btn"
        >Previous</button>
        <span class="page-info">Page {{ pagination.current_page }} / {{ pagination.last_page }}</span>
        <button
          :disabled="pagination.current_page === pagination.last_page"
          @click="loadPage(pagination.current_page + 1)"
          class="page-btn"
        >Next</button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import resourceReviewService from '@/services/resourceReviewService'

const emit = defineEmits(['review'])

const submissions = ref([])
const loading = ref(false)
const error = ref('')
const pagination = ref({ current_page: 1, last_page: 1, total: 0 })

const loadSubmissions = async (page = 1) => {
  loading.value = true
  error.value = ''
  try {
    const res = await resourceReviewService.getSubmissions({ page })
    submissions.value = res.data?.data || []
    pagination.value = {
      current_page: res.data?.current_page || 1,
      last_page: res.data?.last_page || 1,
      total: res.data?.total || 0,
    }
  } catch {
    error.value = 'Failed to load submissions.'
  } finally {
    loading.value = false
  }
}

const loadPage = (page) => loadSubmissions(page)

const truncate = (str, len) => str?.length > len ? str.substring(0, len) + '…' : str

const formatDate = (dateStr) => {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

defineExpose({ loadSubmissions })
onMounted(loadSubmissions)
</script>

<style scoped>
.submissions-list {
  background: white;
  border-radius: 0.75rem;
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.loading {
  padding: 3rem;
  text-align: center;
  color: #6b7280;
}

.error-msg {
  padding: 1rem 1.25rem;
  color: #dc2626;
}

.empty {
  padding: 4rem 2rem;
  text-align: center;
  color: #6b7280;
}

.empty-icon {
  font-size: 2.5rem;
  color: #10b981;
  margin-bottom: 0.75rem;
}

.empty-sub {
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.list-meta {
  padding: 0.75rem 1.25rem;
  font-size: 0.875rem;
  color: #6b7280;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.table th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.table td {
  padding: 0.875rem 1rem;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: top;
}

.table tr:last-child td { border-bottom: none; }

.col-resource { max-width: 280px; }

.resource-title {
  font-weight: 600;
  color: #1f2937;
}

.resource-desc {
  color: #6b7280;
  font-size: 0.8rem;
  margin-top: 0.2rem;
}

.badge-audience {
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
}

.aud-mentors { background: #dbeafe; color: #1e40af; }
.aud-mentees { background: #fce7f3; color: #9d174d; }
.aud-both { background: #d1fae5; color: #065f46; }

.actions {
  white-space: nowrap;
}

.btn-review {
  padding: 0.35rem 0.8rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
}

.btn-review:hover { background: #2563eb; }

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
}

.page-btn {
  padding: 0.4rem 0.9rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
}

.page-btn:hover:not(:disabled) { background: #f9fafb; }
.page-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.page-info {
  font-size: 0.875rem;
  color: #6b7280;
}
</style>
